import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { DragSource, DropTarget } from 'react-dnd';
import {
  Label,
  Input,
  Button,
  UncontrolledTooltip,
} from 'reactstrap';

import ItemTypes from '../../../helpers/DNDItemTypes';
import { filtersTarget, filtersSource } from '../../../helpers/DNDFilters';

const AdminFiltersListItem = ({
  filter,
  saving,
  connectDragSource,
  connectDropTarget,
  isDragging,
  handleVisibilitySwitch,
  handleAdminsOnlySwitch,
  handleOpenByDefaultSwitch,
  taxonomies,
  collapseOpen,
  handleCollapseOpen,
}) => {
  const intl = useIntl();

  const isTaxonomy = /^tax_\d+$/.test(filter.key);
  const isMetadataGroup = /^tax_group_\d+$/.test(filter.key);
  const isMetadataGroupTaxonomy = /^tax_group_\d+_tax_\d+$/.test(filter.key);
  const showCollapseButton = filter.children;

  if (isTaxonomy && !isMetadataGroup) {
    const taxonomy = taxonomies.find(t => `${t.id}` === filter.key.replace('tax_', ''));

    if (taxonomy.metadata_groups.length > 0) {
      return null;
    }
  }

  return connectDragSource(connectDropTarget(
    <tr className={classnames(
      {
        dragging: isDragging,
        metadata_group_row: isMetadataGroup,
        metadata_group_taxonomy_row: isMetadataGroupTaxonomy,
      },
    )}
    >
      <th scope="row" className="filter-name">
        <i className="fa-solid fa-grip-vertical" />
        {showCollapseButton && (
          <Button
            size="sm"
            color="link"
            className="px-1"
            onClick={() => handleCollapseOpen(!collapseOpen)}
          >
            {collapseOpen ?
              <i className="far fa-chevron-up" /> :
              <i className="far fa-chevron-right" />}
          </Button>
        )}
        {isMetadataGroup && (
          <>
            <i
              id={`TagCategoryGroupFilterIcon-${filter.key}`}
              className="far fa-folder-open primary"
            />
            <UncontrolledTooltip
              target={`TagCategoryGroupFilterIcon-${filter.key}`}
            >
              {intl.formatMessage({ defaultMessage: 'Metadata Group' })}
            </UncontrolledTooltip>
          </>
        )}
        {filter.name}
      </th>
      <td className="filter-visible">
        <Label for={`switch-${filter.key}`} className="switch">
          <div className="switch-control input-inner">
            <Input
              type="checkbox"
              name="switchVisibility"
              id={`switch-${filter.key}`}
              disabled={saving}
              value="true"
              checked={filter.visible !== false}
              onChange={() => { handleVisibilitySwitch(filter.key); }}
            />
            <i />
          </div>
        </Label>
      </td>
      <td className="filter-admins-only">
        <Label for={`switch-ao-${filter.key}`} className="switch">
          <div className="switch-control input-inner">
            <Input
              type="checkbox"
              name="switchAdminsOnly"
              id={`switch-ao-${filter.key}`}
              disabled={saving}
              value="true"
              checked={!!filter.admins_only}
              onChange={() => { handleAdminsOnlySwitch(filter.key); }}
            />
            <i />
          </div>
        </Label>
      </td>
      <td className="filter-open-by-default">
        {isMetadataGroup && (
          <Label for={`switch-obd-${filter.key}`} className="switch">
            <div className="switch-control input-inner">
              <Input
                type="checkbox"
                name="switchOpenByDefault"
                id={`switch-obd-${filter.key}`}
                disabled={saving}
                value="true"
                checked={filter.open_by_default !== false}
                onChange={() => { handleOpenByDefaultSwitch(filter.key); }}
              />
              <i />
            </div>
          </Label>
        )}
      </td>
    </tr>,
  ));
};

const mapStateToProps = state => ({
  taxonomies: state.Metadata.taxonomies,
});

AdminFiltersListItem.propTypes = {
  filter: PropTypes.object.isRequired,
  taxonomies: PropTypes.array.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  handleVisibilitySwitch: PropTypes.func.isRequired,
  handleAdminsOnlySwitch: PropTypes.func.isRequired,
  handleOpenByDefaultSwitch: PropTypes.func.isRequired,
  collapseOpen: PropTypes.bool,
  handleCollapseOpen: PropTypes.func,
};

AdminFiltersListItem.defaultProps = {
  collapseOpen: true,
  handleCollapseOpen: () => {},
};

export default connect(mapStateToProps)(
  DragSource(
    ItemTypes.FILTER,
    filtersSource,
    (dragConnect, monitor) => ({
      connectDragSource: dragConnect.dragSource(),
      isDragging: monitor.isDragging(),
    }),
  )(
    DropTarget(
      ItemTypes.FILTER,
      filtersTarget,
      dropConnect => ({
        connectDropTarget: dropConnect.dropTarget(),
      }),
    )(AdminFiltersListItem),
  ),
);
